import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutContent } from '@/mocks/about';

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('sending');
    setFormError('');

    try {
      const formData = new FormData(form);
      const submitAddr = 'https://readdy.ai/api/form/d9qr9nue3oq4jqumtph0';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="d9qr9nue3oq4jqumtph0">
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            name="name"
            placeholder="您的姓名"
            required
            className="px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
          <input
            type="email"
            name="email"
            placeholder="电子邮箱"
            required
            className="px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>
        <input
          type="text"
          name="subject"
          placeholder="主题"
          className="px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
        />
        <textarea
          name="message"
          placeholder="请输入您的消息（最多500字）"
          rows={4}
          maxLength={500}
          required
          className="px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
        ></textarea>

        <input
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="contact-hp"
        />

        <button
          type="submit"
          disabled={formStatus === 'sending'}
          className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {formStatus === 'sending' ? '发送中...' : '发送消息'}
        </button>

        {formStatus === 'success' && (
          <p className="text-sm text-secondary-600">消息已成功发送，我们会尽快回复您！</p>
        )}
        {formStatus === 'error' && (
          <p className="text-sm text-primary-600">{formError}</p>
        )}
      </div>
    </form>
  );
}

export default function AboutPage() {
  const { brandIntro, reputation, contact } = aboutContent;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩国伦理文化研究',
    description: brandIntro.paragraphs[0],
    url: window.location.href,
    about: {
      '@type': 'Organization',
      name: '韩国伦理文化研究',
      description: brandIntro.paragraphs[0],
      foundingDate: '2018',
      contactPoint: {
        '@type': 'ContactPoint',
        email: contact.email,
        contactType: 'customer service',
      },
    },
  };

  return (
    <>
      <SEO
        title="关于我们 - 韩国伦理文化研究"
        description="韩国伦理文化研究创立于2018年，是专注于韩国伦理文化深度解析的中文内容平台。了解我们的品牌故事、学术声誉与联系方式。"
        keywords="韩国伦理,关于我们,品牌介绍,韩国伦理文化研究,联系我们"
        canonicalUrl="https://korea-ethics.com/about"
        schema={pageSchema}
      />

      <Navbar />
      <ScrollToTop />

      <main className="min-h-screen pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <div className="relative rounded-2xl overflow-hidden mb-12 h-48 md:h-64">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Korean%20traditional%20scholarly%20library%20with%20wooden%20bookshelves%20filled%20with%20ancient%20texts%2C%20warm%20ambient%20lighting%2C%20serene%20study%20atmosphere%2C%20cultural%20research%20institute%2C%20elegant%20hanok%20interior%20with%20modern%20touches&width=1200&height=500&seq=about-header&orientation=landscape')`,
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/20"></div>
            <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-10">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-2">
                关于我们
              </h1>
              <p className="text-background-50/80 text-sm">了解韩国伦理文化研究的故事与使命</p>
            </div>
          </div>

          <section id="brand" className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              {brandIntro.title}
            </h2>
            <div className="flex flex-col gap-4 text-foreground-700 leading-relaxed text-sm md:text-base">
              {brandIntro.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-flag-line text-primary-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">
                    我们的使命
                  </h3>
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed">{brandIntro.mission}</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-eye-line text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">
                    我们的愿景
                  </h3>
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed">{brandIntro.vision}</p>
              </div>
            </div>
          </section>

          <section id="reputation" className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              {reputation.title}
            </h2>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              {reputation.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-xl p-5 text-center border border-background-200"
                >
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-foreground-500 text-xs">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {reputation.testimonials.map((t) => (
                <div
                  key={t.author}
                  className="bg-background-50 rounded-xl p-5 border border-background-200"
                >
                  <i className="ri-double-quotes-l text-accent-400 text-2xl mb-3 block"></i>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">{t.quote}</p>
                  <div className="border-t border-background-200 pt-3">
                    <div className="text-sm font-semibold text-foreground-900">{t.author}</div>
                    <div className="text-xs text-foreground-400">{t.role}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-background-100 rounded-xl p-6 border border-background-200">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                荣誉与成就
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {reputation.achievements.map((ach, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-check-line text-accent-600 text-xs"></i>
                    </div>
                    <span className="text-foreground-600 text-sm">{ach}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              {contact.title}
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900 mb-0.5">电子邮箱</div>
                    <div className="text-sm text-foreground-500">{contact.email}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900 mb-0.5">办公地址</div>
                    <div className="text-sm text-foreground-500">{contact.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900 mb-0.5">工作时间</div>
                    <div className="text-sm text-foreground-500">{contact.workingHours}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-wechat-line text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900 mb-0.5">微信公众号</div>
                    <div className="text-sm text-foreground-500">{contact.wechat}</div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 border border-background-200">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                  发送消息
                </h3>
                <ContactForm />
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}